/**
 * Geo-referenced design canvas: live Esri World Imagery tiles, pan/zoom,
 * multi-surface boundary & obstacle drawing, rotated module grids and
 * drag-to-move modules. Layout preview uses the SAME @pv/engine autoLayout
 * as the server — client preview and server simulation cannot diverge.
 */
import { useEffect, useRef } from "react";
import {
  autoLayout,
  rotatePoint,
  type LayoutOptions,
  type PvModuleSpec,
  type Surface,
} from "@pv/engine";

/** E37: canvas cannot use var() directly — read tokens from the loaded :root
 *  palette at draw time. styles.css remains the single source of truth. */
const themeCache: Record<string, string> = {};
function tk(name: string): string {
  if (!themeCache[name]) {
    themeCache[name] = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }
  return themeCache[name]!;
}

const CW = 940;
const CH = 560;
const RE = 6378137;
const TILE = (z: number, y: number, x: number) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

export interface View {
  S: number; // px per metre
  cx: number;
  cy: number;
}

export interface CanvasGeometry {
  vertices: { x: number; y: number }[];
  obstacles: { center: { x: number; y: number }; radiusM: number; heightM: number; kind: string }[];
}

export interface CanvasSurface {
  id: string;
  geometry: CanvasGeometry;
  rotationDeg: number;
  edgeSetbackM: number;
  kind: string;
  /** Layout preview overrides for this surface. */
  removedKeys?: Set<string>;
  movedOffsets?: Record<string, { dx: number; dy: number }>;
}

export type Tool = "draw" | "obstacle" | "module";

interface Props {
  lat: number;
  lon: number;
  surfaces: CanvasSurface[];
  activeSurfaceId: string | null;
  view: View;
  tool: Tool;
  mode: "site" | "layout";
  module?: PvModuleSpec | null;
  layoutOptions?: LayoutOptions;
  onGeometryChange?: (surfaceId: string, g: CanvasGeometry) => void;
  onViewChange: (v: View) => void;
  onToggleModule?: (surfaceId: string, key: string) => void;
  onMoveModule?: (surfaceId: string, key: string, dx: number, dy: number) => void;
  onModuleStats?: (perSurface: Record<string, { count: number; kwp: number }>) => void;
}

const tileCache = new Map<string, HTMLImageElement>();

function ll2m(lat: number, lon: number): { x: number; y: number } {
  return { x: (RE * lon * Math.PI) / 180, y: RE * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) };
}

export function fitViewFor(surfaces: { geometry: CanvasGeometry }[]): View {
  const all = surfaces.flatMap((s) => s.geometry.vertices);
  if (all.length < 3) return { S: 12, cx: 27, cy: 17 };
  const xs = all.map((p) => p.x);
  const ys = all.map((p) => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const S = Math.max(
    2,
    Math.min(CW / (Math.max(...xs) - Math.min(...xs) + 14), CH / (Math.max(...ys) - Math.min(...ys) + 14), 22),
  );
  return { S: +S.toFixed(2), cx, cy };
}

function toEngineSurface(s: CanvasSurface): Surface {
  return {
    id: s.id,
    kind: s.kind as Surface["kind"],
    boundary: { vertices: s.geometry.vertices },
    obstacles: s.geometry.obstacles.map((o) => ({
      center: o.center,
      radiusM: o.radiusM,
      heightM: o.heightM,
      kind: o.kind as Surface["obstacles"][number]["kind"],
    })),
    azimuthDeg: 180,
    tiltDeg: 35,
    edgeSetbackM: s.edgeSetbackM,
    obstacleBufferM: 0.25,
    gridRotationDeg: s.rotationDeg,
  };
}

export default function SiteCanvas(props: Props): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef(props);
  stateRef.current = props;
  /** Live drag state for module moving (not yet committed). */
  const dragRef = useRef<{
    surfaceId: string;
    key: string;
    startLocal: { x: number; y: number };
    base: { dx: number; dy: number };
    live: { dx: number; dy: number };
  } | null>(null);

  useEffect(() => {
    draw();
  });

  function layoutFor(s: CanvasSurface, liveDrag = true) {
    const p = stateRef.current;
    if (!p.module || s.geometry.vertices.length < 3) return null;
    const moved = { ...(s.movedOffsets ?? {}) };
    const d = dragRef.current;
    if (liveDrag && d && d.surfaceId === s.id) {
      moved[d.key] = { dx: d.base.dx + d.live.dx, dy: d.base.dy + d.live.dy };
    }
    return autoLayout(toEngineSurface(s), p.module, p.layoutOptions ?? { orientation: "portrait", moduleGapM: 0.02, rowGapM: 0 }, {
      removedKeys: s.removedKeys,
      movedOffsets: moved,
    });
  }

  function draw(): void {
    const cv = ref.current;
    if (!cv) return;
    const p = stateRef.current;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    cv.width = CW;
    cv.height = CH;
    const { S, cx, cy } = p.view;
    const M = (q: { x: number; y: number }) => ({ x: (q.x - cx) * S + CW / 2, y: (q.y - cy) * S + CH / 2 });

    ctx.fillStyle = tk("--terrain");
    ctx.fillRect(0, 0, CW, CH);

    // tiles
    const cosL = Math.cos((p.lat * Math.PI) / 180);
    const aw = ll2m(p.lat, p.lon);
    const AX = 27;
    const AY = 17;
    const z = Math.max(14, Math.min(19, Math.round(Math.log2((2 * Math.PI * RE * cosL * S) / 256))));
    const mpt = (2 * Math.PI * RE) / Math.pow(2, z);
    const x0 = cx - CW / 2 / S;
    const y0 = cy - CH / 2 / S;
    const m0x = aw.x + (x0 - AX) / cosL;
    const m0y = aw.y - (y0 - AY) / cosL;
    const ppm = cosL * S;
    const tpx = mpt * ppm;
    const tx0 = Math.floor((m0x + Math.PI * RE) / mpt);
    const ty0 = Math.floor((Math.PI * RE - m0y) / mpt);
    for (let tx = tx0; tx <= tx0 + Math.ceil(CW / tpx); tx++) {
      for (let ty = ty0; ty <= ty0 + Math.ceil(CH / tpx); ty++) {
        const key = `${z}/${ty}/${tx}`;
        let img = tileCache.get(key);
        if (!img) {
          img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => draw();
          img.src = TILE(z, ty, tx);
          tileCache.set(key, img);
        }
        if (img.complete && img.naturalWidth) {
          const mx = tx * mpt - Math.PI * RE;
          const my = Math.PI * RE - ty * mpt;
          ctx.drawImage(img, (mx - m0x) * ppm, (m0y - my) * ppm, tpx + 0.8, tpx + 0.8);
        }
      }
    }

    const stats: Record<string, { count: number; kwp: number }> = {};

    for (const s of p.surfaces) {
      const isActive = s.id === p.activeSurfaceId;
      const g = s.geometry;

      // boundary
      if (g.vertices.length >= 2) {
        ctx.beginPath();
        const a = M(g.vertices[0]!);
        ctx.moveTo(a.x, a.y);
        g.vertices.slice(1).forEach((q) => {
          const c = M(q);
          ctx.lineTo(c.x, c.y);
        });
        if (g.vertices.length > 2) {
          ctx.closePath();
          ctx.fillStyle = isActive ? tk("--roof-fill") : tk("--roof-fill-soft");
          ctx.fill();
        }
        ctx.strokeStyle = isActive ? tk("--canvas-active") : tk("--canvas-active-soft");
        ctx.lineWidth = isActive ? 2.5 : 1.5;
        ctx.stroke();
      }

      // modules (rotated grid → draw world-frame polygons)
      if (p.mode === "layout" && p.module && g.vertices.length > 2) {
        const layout = layoutFor(s);
        if (layout) {
          let active = 0;
          const rot = layout.gridRotationDeg;
          const pivot = layout.gridPivot;
          for (const m of layout.modules) {
            const removed = s.removedKeys?.has(m.key);
            if (!removed) active++;
            const cornersGrid = [
              { x: m.x, y: m.y },
              { x: m.x + m.w, y: m.y },
              { x: m.x + m.w, y: m.y + m.h },
              { x: m.x, y: m.y + m.h },
            ];
            const corners = cornersGrid.map((c) => M(rot ? rotatePoint(c, pivot, rot) : c));
            ctx.beginPath();
            ctx.moveTo(corners[0]!.x, corners[0]!.y);
            for (let i = 1; i < 4; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
            ctx.closePath();
            ctx.fillStyle = removed
              ? tk("--canvas-contrast-faint")
              : m.invalid
                ? tk("--panel-invalid-fill")
                : tk("--panel-fill");
            ctx.strokeStyle = removed ? tk("--canvas-contrast-soft") : m.invalid ? tk("--panel-invalid") : tk("--panel-stroke");
            ctx.lineWidth = 1;
            ctx.fill();
            ctx.stroke();
          }
          stats[s.id] = { count: active, kwp: (active * p.module.wp) / 1000 };
        }
      }

      // obstacles
      for (const o of g.obstacles) {
        const q = M(o.center);
        ctx.fillStyle = tk("--obstacle");
        ctx.beginPath();
        ctx.arc(q.x, q.y, o.radiusM * S, 0, 7);
        ctx.fill();
        ctx.strokeStyle = tk("--canvas-contrast");
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // draw-mode vertices for the active surface
      if (p.mode === "site" && isActive) {
        g.vertices.forEach((pt, i) => {
          const q = M(pt);
          ctx.fillStyle = i === 0 ? tk("--canvas-active") : tk("--canvas-contrast");
          ctx.strokeStyle = tk("--canvas-ink");
          ctx.beginPath();
          ctx.arc(q.x, q.y, 5, 0, 7);
          ctx.fill();
          ctx.stroke();
        });
      }

      // surface label
      if (g.vertices.length > 2) {
        const cxm = g.vertices.reduce((a, v) => a + v.x, 0) / g.vertices.length;
        const cym = Math.min(...g.vertices.map((v) => v.y));
        const q = M({ x: cxm, y: cym });
        ctx.font = "bold 11px sans-serif";
        ctx.fillStyle = isActive ? tk("--canvas-active") : tk("--canvas-contrast-strong");
        ctx.fillText(s.id === p.activeSurfaceId ? `▸ ${labelOf(s)}` : labelOf(s), q.x - 20, q.y - 8);
      }
    }

    if (p.mode === "layout") p.onModuleStats?.(stats);

    // north + scale + attribution
    ctx.fillStyle = tk("--canvas-contrast-max");
    ctx.strokeStyle = tk("--canvas-ink");
    ctx.beginPath();
    ctx.moveTo(30, 44);
    ctx.lineTo(24, 62);
    ctx.lineTo(30, 56);
    ctx.lineTo(36, 62);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.font = "bold 12px sans-serif";
    ctx.fillText("N", 25, 36);
    const L = S > 18 ? 2 : S > 7 ? 5 : S > 3 ? 20 : 50;
    ctx.fillRect(20, CH - 44, S * L, 5);
    ctx.font = "11px sans-serif";
    ctx.fillText(`${L} m`, 20 + (S * L) / 2 - 8, CH - 50);
    ctx.fillStyle = tk("--canvas-contrast-strong");
    ctx.font = "10px sans-serif";
    ctx.fillText(`Esri World Imagery · ${p.lat.toFixed(4)}, ${p.lon.toFixed(4)}`, CW - 240, CH - 10);
  }

  function labelOf(s: CanvasSurface): string {
    return s.id.slice(0, 4) === "srf-" ? s.id : s.id.slice(0, 6);
  }

  function toLocal(ev: { clientX: number; clientY: number }): { x: number; y: number } {
    const cv = ref.current!;
    const r = cv.getBoundingClientRect();
    const { S, cx, cy } = stateRef.current.view;
    const px = (ev.clientX - r.left) * (CW / r.width);
    const py = (ev.clientY - r.top) * (CH / r.height);
    return { x: cx + (px - CW / 2) / S, y: cy + (py - CH / 2) / S };
  }

  /** Hit-test modules of a surface at a world point (grid-frame transform). */
  function hitModule(s: CanvasSurface, world: { x: number; y: number }) {
    const layout = layoutFor(s, false);
    if (!layout) return null;
    const gp =
      layout.gridRotationDeg !== 0
        ? rotatePoint(world, layout.gridPivot, -layout.gridRotationDeg)
        : world;
    return layout.modules.find(
      (m) => gp.x >= m.x && gp.x <= m.x + m.w && gp.y >= m.y && gp.y <= m.y + m.h,
    ) ?? null;
  }

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    let down: { x: number; y: number } | null = null;
    let moved = false;

    const onDown = (ev: MouseEvent) => {
      down = { x: ev.clientX, y: ev.clientY };
      moved = false;
      const p = stateRef.current;
      if (p.mode === "layout" && p.onMoveModule && p.activeSurfaceId) {
        const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
        if (s) {
          const world = toLocal(ev);
          const hit = hitModule(s, world);
          if (hit && !s.removedKeys?.has(hit.key)) {
            const layout = layoutFor(s, false)!;
            const startLocal =
              layout.gridRotationDeg !== 0
                ? rotatePoint(world, layout.gridPivot, -layout.gridRotationDeg)
                : world;
            dragRef.current = {
              surfaceId: s.id,
              key: hit.key,
              startLocal,
              base: s.movedOffsets?.[hit.key] ?? { dx: 0, dy: 0 },
              live: { dx: 0, dy: 0 },
            };
          }
        }
      }
    };

    const onMove = (ev: MouseEvent) => {
      if (!down) return;
      const p = stateRef.current;
      const dx = ev.clientX - down.x;
      const dy = ev.clientY - down.y;
      if (!moved && Math.abs(dx) + Math.abs(dy) <= 4) return;
      moved = true;

      const d = dragRef.current;
      if (d) {
        // dragging a module: convert cursor to grid frame of that surface
        const s = p.surfaces.find((x) => x.id === d.surfaceId);
        if (s) {
          const layout = layoutFor(s, false)!;
          const world = toLocal(ev);
          const gp =
            layout.gridRotationDeg !== 0
              ? rotatePoint(world, layout.gridPivot, -layout.gridRotationDeg)
              : world;
          d.live = { dx: gp.x - d.startLocal.x, dy: gp.y - d.startLocal.y };
          draw();
        }
        return;
      }

      // panning
      const r = cv.getBoundingClientRect();
      const k = CW / r.width;
      p.onViewChange({ ...p.view, cx: p.view.cx - (dx * k) / p.view.S, cy: p.view.cy - (dy * k) / p.view.S });
      down = { x: ev.clientX, y: ev.clientY };
    };

    const onUp = (ev: MouseEvent) => {
      const was = moved;
      const d = dragRef.current;
      dragRef.current = null;
      down = null;
      moved = false;
      const p = stateRef.current;

      if (d && was) {
        // commit module move (snap to 1 cm)
        const dx = Math.round((d.base.dx + d.live.dx) * 100) / 100;
        const dy = Math.round((d.base.dy + d.live.dy) * 100) / 100;
        p.onMoveModule?.(d.surfaceId, d.key, dx, dy);
        return;
      }
      if (was) return; // pan ended

      // click actions
      const world = toLocal(ev);
      if (p.mode === "layout") {
        if (p.activeSurfaceId && p.onToggleModule) {
          const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
          if (s) {
            const hit = hitModule(s, world);
            if (hit) p.onToggleModule(s.id, hit.key);
          }
        }
        return;
      }
      // site mode drawing on the active surface
      if (!p.activeSurfaceId || !p.onGeometryChange) return;
      const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
      if (!s) return;
      if (p.tool === "draw") {
        const v = s.geometry.vertices;
        if (v.length > 2) {
          const a = v[0]!;
          if (Math.hypot(world.x - a.x, world.y - a.y) * p.view.S < 12) return;
        }
        p.onGeometryChange(s.id, {
          ...s.geometry,
          vertices: [...v, { x: +world.x.toFixed(2), y: +world.y.toFixed(2) }],
        });
      } else if (p.tool === "obstacle") {
        p.onGeometryChange(s.id, {
          ...s.geometry,
          obstacles: [
            ...s.geometry.obstacles,
            { center: { x: +world.x.toFixed(2), y: +world.y.toFixed(2) }, radiusM: 0.6, heightM: 1.2, kind: "chimney" },
          ],
        });
      }
    };

    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const p = stateRef.current;
      const S = Math.max(1.5, Math.min(45, p.view.S * Math.exp(-ev.deltaY * 0.0012)));
      p.onViewChange({ ...p.view, S });
    };

    const onCtx = (ev: MouseEvent) => {
      ev.preventDefault();
      const p = stateRef.current;
      if (p.mode !== "site" || !p.activeSurfaceId || !p.onGeometryChange) return;
      const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
      if (!s) return;
      const q = toLocal(ev);
      p.onGeometryChange(s.id, {
        ...s.geometry,
        obstacles: s.geometry.obstacles.filter(
          (o) => Math.hypot(o.center.x - q.x, o.center.y - q.y) > o.radiusM + 0.3,
        ),
      });
    };

    cv.addEventListener("mousedown", onDown);
    cv.addEventListener("mousemove", onMove);
    cv.addEventListener("mouseup", onUp);
    cv.addEventListener("wheel", onWheel, { passive: false });
    cv.addEventListener("contextmenu", onCtx);
    return () => {
      cv.removeEventListener("mousedown", onDown);
      cv.removeEventListener("mousemove", onMove);
      cv.removeEventListener("mouseup", onUp);
      cv.removeEventListener("wheel", onWheel);
      cv.removeEventListener("contextmenu", onCtx);
    };
  }, []);

  return <canvas ref={ref} className="designer" />;
}
